import { finishCloseModal, startCloseModal, updateModal } from '../../actions/app'
import { Loader } from '.'

const MODAL_CLOSE_EVENT = 'apricot_modalClose'
const MODAL_CLOSE_START_EVENT = 'apricot_modalClose_start'
const MODAL_DOM_PATH = '#root .modal-container .cb-modal'

const ModalHeader = ({ title, showCloseButton, modalId }) => (
  <div className={`cb-modal-header${showCloseButton ? ' cb-modal-has-close' : ''}`}>
    {title ? (
      <h2 className="cb-modal-title" id={`${modalId}Title`}>
        {title}
      </h2>
    ) : null}
    {showCloseButton ? (
      <button
        type="button"
        className="close cb-btn-close"
        aria-describedby={title ? `${modalId}Title` : undefined}
        data-cb-modal-close
      >
        <span aria-hidden="true">×</span>
        <span className="sr-only">Close</span>
      </button>
    ) : null}
  </div>
)

// actions = { isDismissable, onClick, isPrimary, buttonLabel, isDisabled }
const ModalFooter = ({ actions, unsetModalIsCancelled }) => (
  <div className="cb-modal-footer">
    <div className="btn-row">
      {actions.map(a => {
        const dismissable = a.isDismissable ? { 'data-cb-modal-close': true } : {}
        return (
          <button
            type="button"
            key={a.buttonLabel}
            onClick={() => {
              if (!a.onClick) return
              unsetModalIsCancelled()
              a.onClick()
            }}
            className={`btn btn-sm ${a.isPrimary ? 'btn-primary' : 'btn-secondary'}`}
            {...dismissable}
            disabled={a.isDisabled}
            style={{ position: 'relative' }}
          >
            {a.buttonLabel}
            {a.busy && (
              <Loader size="sm" style={{ position: 'absolute', top: '12px', right: '10px' }} />
            )}
          </button>
        )
      })}
    </div>
  </div>
)

const mapStateToProps = state => ({
  isLoggedIn: state.user.isAuthed,
  modal: state.app.modal,
  breakpoint: state.app.breakpoint.name,
})
const mapDispatchToProps = dispatch =>
  bindActionCreators({ startCloseModal, finishCloseModal, updateModal }, dispatch)

class Modal extends Component {
  constructor(props) {
    const { modal } = props
    super(props)
    this.modalName = modal.name
    this.modalIsCancelled = true
    this.modal = createRef()
    this.state = { modalId: `modal${modal.name}` }
    this.wrapOnClose = this.wrapOnClose.bind(this)
    this.startOnClose = this.startOnClose.bind(this)
  }

  componentDidMount() {
    const { modalId } = this.state

    APRICOT.CBModal.init({ elem: this.modal.current, controlled: true })
    APRICOT.CBModal.show({ targetModal: modalId })
    const modalElem = document.querySelector(MODAL_DOM_PATH)
    modalElem?.addEventListener(MODAL_CLOSE_EVENT, this.wrapOnClose)
    modalElem?.addEventListener(MODAL_CLOSE_START_EVENT, this.startOnClose)

    this.IOS11WorkAroundForInputFocusBug(true)
  }

  shouldComponentUpdate(nextProps) {
    return !nextProps.modal.transitioning
  }

  componentDidUpdate(prevProps) {
    const { startCloseModal, isLoggedIn, shouldCloseModal, children, updateModal } = this.props
    if (shouldCloseModal || !isLoggedIn) {
      startCloseModal()
      APRICOT.CBModal.close({ elem: this.modal.current })
    }

    if (children.type !== prevProps.children?.type) {
      updateModal()
    }
  }

  componentWillUnmount() {
    const modalElem = document.querySelector(MODAL_DOM_PATH)
    modalElem?.removeEventListener(MODAL_CLOSE_EVENT, this.wrapOnClose)
    modalElem?.removeEventListener(MODAL_CLOSE_START_EVENT, this.startOnClose)

    this.IOS11WorkAroundForInputFocusBug()
  }

  wrapOnClose() {
    const { onCloseAction, modalCloseFocusElem, finishCloseModal } = this.props
    onCloseAction && onCloseAction() // Called every time a modal is dismissed
    modalCloseFocusElem && modalCloseFocusElem.focus()
    finishCloseModal(this.modalName)
  }

  startOnClose() {
    const { onCancelOnlyAction } = this.props
    // Called only if onCancelOnlyAction exists and dismissing action does not have an associated click value
    this.modalIsCancelled && onCancelOnlyAction && onCancelOnlyAction()
  }

  IOS11WorkAroundForInputFocusBug(show) {
    const { breakpoint } = this.props
    // https://stackoverflow.com/questions/46339063/ios-11-safari-bootstrap-modal-text-area-outside-of-cursor
    const smallBreakpoint = breakpoint === 'mobile' || breakpoint === 'tablet'
    document.body.style.position = show && smallBreakpoint ? 'fixed' : 'relative'
  }

  unsetModalIsCancelled() {
    this.modalIsCancelled = false
  }

  render() {
    const { headerTitle, children, footerActions, noHeader, hideCloseButton } = this.props
    const { modalId } = this.state

    return (
      <div className="cb-modal" id={modalId} ref={this.modal}>
        <div className="cb-modal-overlay" tabIndex={-1} data-cb-modal-close>
          <div
            className="cb-modal-container"
            role="dialog"
            aria-modal="true"
            aria-labelledby={headerTitle && !noHeader ? `${modalId}Title` : undefined}
          >
            {!noHeader ? (
              <ModalHeader
                title={headerTitle}
                showCloseButton={!hideCloseButton}
                modalId={modalId}
              />
            ) : null}
            <div className="cb-modal-content">{children}</div>
            {footerActions ? (
              <ModalFooter
                actions={footerActions}
                unsetModalIsCancelled={this.unsetModalIsCancelled.bind(this)}
              />
            ) : null}
          </div>
        </div>
      </div>
    )
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(Modal)
